import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ChatMessage from './ChatMessage';
import MessageInput from './MessageInput';
import EmptyState from './EmptyState';
import { apiFetch } from 'utils/api';

export default function ChatPage({ chatId, chatTitle, onAutoRename }) {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [didAutoRename, setDidAutoRename] = useState(false);

  useEffect(() => {
    if (!chatId) return;
    loadMessages();
  }, [chatId]);

  useEffect(() => {
    setDidAutoRename(false);
  }, [chatId]);

  const loadMessages = async () => {
    try {
      const data = await apiFetch(`/api/messages/${chatId}`);
      setMessages(data || []);
    } catch (err) {
      console.error('Failed to load messages', err);
    }
  };

  const handleSend = async (text) => {
    if (!text || !text.trim()) return;
    const shouldAutoRename = chatTitle === 'New Chat' && !didAutoRename && text.trim().length > 10;

    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    setIsTyping(true);

    try {
      const token = localStorage.getItem('token');

      const res = await fetch(`/api/messages/${chatId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: text })
      });

      const data = await res.json();

      if (shouldAutoRename) {
        try {
          const result = await apiFetch(`/api/chats/${chatId}/auto-title`, {
            method: 'POST',
            body: JSON.stringify({ message: text })
          });

          onAutoRename?.(chatId, result.title);
          setDidAutoRename(true);
        } catch (err) {
          console.error('AI auto title failed', err);
        }
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      console.error(err);
    } finally {
      setIsTyping(false);
    }
  };

  if (!chatId) return <EmptyState />;

  return (
    <Box flex={1} display="flex" flexDirection="column" justifyContent="space-between" p={3} height="100%">
      <Box flex={1} overflow="auto">
        {messages.map((msg, index) => (
          <Box
            key={msg.id ?? `${msg.role}-${index}`}
            display="flex"
            justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
            mb={3}
          >
            <ChatMessage content={msg.content} isUser={msg.role === 'user'} />
          </Box>
        ))}

        {/* 🔹 TYPING INDICATOR */}
        {isTyping && (
          <Box display="flex" justifyContent="flex-start" mb={3}>
            <ChatMessage isTyping />
          </Box>
        )}
      </Box>
      <Box flexShrink={0}>
        <MessageInput onSend={handleSend} />
      </Box>
    </Box>
  );
}
